import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stopwatch from "components/Shared/StopWatch";
import {  MSpinner } from "components/Shared";
import { CaseTimeLogWorkTypeEnum } from "interfaces/Mueller.Business.Models.ts";
import { manageFormsRedux } from "requests/homeowner/ho";
import { getCaseForms } from "actions/qualityAssurance";
import { setCallCenterReady, setCallCenterControlNumber } from "actions/callCenter";
import { moveToMap, getNextCase, leaveCase } from "requests/schedulingAssistant";
import { usePhoenixForms } from "components/PhoenixFormsV4/index.tsx";
import { CASE_FORMS_MAINTENANCE } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";

const SchedulingAssistantHeader = (props) => {
  const {
    history,
    controlNumber,
    internetUserId,
    setCallCenterReady,
    setCallCenterControlNumber,
    beginManageForms,
  } = props;

  const mounted = useRef(true);
  const controlNumberOnLeave = useRef(null);
  const timeSpentOnLeave = useRef(0);
  const [startClicked, setStartClicked] = useState(false);
  const [working, setWorking] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [showMoveToMapDialog, setShowMoveToMapDialog] = useState(false);
  const [movingToMap, setMovingToMap] = useState(false);
  const [showNeedCallDialog, setShowNeedCallDialog] = useState(false);

  const { needsSave } = usePhoenixForms();

  useEffect(() => {
    controlNumberOnLeave.current = controlNumber;
    timeSpentOnLeave.current = timeSpent;
  }, [controlNumber, timeSpent]);

  const leavingPage = () => {
    if(controlNumberOnLeave.current){
      leaveCase({
        ControlNumber: controlNumberOnLeave.current,
        TimeLog: {
          controlIdNumber: controlNumberOnLeave.current,
          internetUserId: internetUserId,
          workDate: moment().format("YYYY-MM-DD"),
          workTypeId: CaseTimeLogWorkTypeEnum.FS_SCHEDULING,
          timeMinutes: Number((timeSpentOnLeave.current / 60)),
        },
      }, () => { }, () => { }, mounted);
    }
    setCallCenterControlNumber(null);
    setCallCenterReady(false);
  };

  useEffect(() => {
    setCallCenterReady(startClicked);
  }, [startClicked]);

  useEffect(() => {
    const { location } = history;
    if (location.state && location.state.controlNumber) {
      getNextCaseForSchedulingAssistant(location.state.controlNumber);
    }

    return () => {
      leavingPage();
      mounted.current = false;
      controlNumberOnLeave.current = null;
      timeSpentOnLeave.current = 0;
    };
  }, []);

  const getNextCaseForSchedulingAssistant = (controlNumber) => {
    setStartClicked(true);
    getNextCase({ ControlNumber: controlNumber }, (resp) => {
      if(resp.controlIdNumber != props.controlNumber){
        setWorking(false);        
        setCallCenterControlNumber(resp.controlIdNumber);
        setWorking(true);        
      } else {
        // Received same control number back, probably need to log a call
        setShowNeedCallDialog(true);
      }
    }, () => {
      setCallCenterControlNumber(null);
      setStartClicked(false);
      setWorking(false);
    }, mounted);
  };

  const moveCaseToMap = () => {
    setShowMoveToMapDialog(false);
    setMovingToMap(true);

    moveToMap({
      ControlNumbers: [controlNumber],
      TimeLog: {
        controlIdNumber: controlNumber,
        internetUserId: internetUserId,
        workDate: moment().format("YYYY-MM-DD"),
        workTypeId: CaseTimeLogWorkTypeEnum.FS_SCHEDULING,
        timeMinutes: Number((timeSpent / 60)),
      },
    }, () => {
      setMovingToMap(false);
      getNextCaseForSchedulingAssistant(null);
    }, () => setMovingToMap(false), mounted);
  };

  const headerButtons = [
    {
      label: "Move To Map",
      action: () => setShowMoveToMapDialog(true),
      disabled: movingToMap || !working
    },
    {
      label: "Get Next Case",
      action: () => {
        getNextCaseForSchedulingAssistant(null);
      },
      disabled: !working
    }
  ];

  // The manage forms dialog is on MCaseView, so we need to be able to enable it even though the dialog is not present here.
  // beginManageForms updates global state so that the dialog displays
  if (props.canEditCaseForms) {
    headerButtons.push(
      {
        label: "Manage Forms",
        action: () => {
          beginManageForms(true);
        },
        disabled: !working || needsSave,
        tooltipLabel: needsSave ? "Unsaved Form Changes!" : null,
      }
    );
  }

  const movingToMapActions = [
    <Button
      className="m-t-10"
      key="continue"
      disabled={false}
      onClick={() => moveCaseToMap()}
    >
      Continue
    </Button>,
    <Button
      className="m-t-10"
      key="cancel"
      onClick={() => setShowMoveToMapDialog(false)}
    >
      Cancel
    </Button>
  ];

  const needCallActions = [
    <Button
      className="m-t-10"
      key="continue"
      disabled={false}
      onClick={() => setShowNeedCallDialog(false)}
    >
      Okay
    </Button>
  ];

  return (
    <div className="fs-scheduling-header">
      <div className="time-container" style={{ visibility: working ? "visible" : "hidden" }}>
        <div className="timer-label">{"Time on Case"}</div>
        <Stopwatch
          key={working}
          hideStopStart
          tickOnMount={working}
          whiteFont
          onChange={secondsElapsed => {
            setTimeSpent(secondsElapsed);
          }}
          paused={false} //{completing || escalating || !working}
        />
      </div>
      <div
        className="case-action-buttons"
        style={
          working ? { visibility: "visible", display: "flex" } : { visibility: "hidden" }
        }
      >
        {headerButtons.map((btn) =>
          <Button
            key={btn.label}
            disabled={btn.disabled}
            title={btn.tooltipLabel}
            onClick={btn.action}
          >
            {btn.label}
          </Button>
        )}
      </div>
      <div
        className="start-fs-scheduling-button"
        style={
          working ? { visibility: "hidden" } : { visibility: "visible" }
        }
      >
        <Button
          className="item"
          key={"start-call-center"}
          disabled={startClicked}
          // disabled={!(activeClock && activeClock.clockInDate) || working}
          onClick={() => {
            getNextCaseForSchedulingAssistant(null);
          }}
        >
          Start Call Center
        </Button>
      </div>
      <div id="move-to-map-container">
        <Dialog
          open={showMoveToMapDialog}
          onClose={() => setShowMoveToMapDialog(false)}
          className="dialog-move-to-map"
          maxWidth={false}
          PaperProps={{ style: { width: 500 } }}
        >
          <DialogTitle>Move Case to Map</DialogTitle>
          <DialogContent>
            This will leave the Scheduling Workflow, are you sure?
          </DialogContent>
          <DialogActions>{movingToMapActions}</DialogActions>
        </Dialog>
        <Dialog
          open={movingToMap}
          maxWidth={false}
          PaperProps={{ style: { width: 400 } }}
        >
          <DialogTitle>Moving case to Map...</DialogTitle>
          <DialogContent>
            <MSpinner />
          </DialogContent>
        </Dialog>    
      </div>
      <Dialog
        open={showNeedCallDialog}
        onClose={() => setShowNeedCallDialog(false)}
        maxWidth={false}
        PaperProps={{ style: { width: 500 } }}
      >
        <DialogTitle>Unable to get next case</DialogTitle>
        <DialogContent>
          Please log a call before getting a new case
        </DialogContent>
        <DialogActions>{needCallActions}</DialogActions>
      </Dialog>          
    </div>
  );
};

SchedulingAssistantHeader.propTypes = {
  controlNumber: PropTypes.number,
  timeSpent: PropTypes.number,
  activeClock: PropTypes.object,
  rights: PropTypes.array,
  internetUserId: PropTypes.number,
  history: PropTypes.object,
  setCallCenterReady: PropTypes.func,
  setCallCenterControlNumber: PropTypes.func,
  beginManageForms: PropTypes.func,
  canEditCaseForms: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    activeClock: state.user.activeClock,
    rights: state.user.profile.rights,
    internetUserId: state.user.profile.internetUserId,
    controlNumber: state.schedulingAssistant.currentControlNumber,
    timeSpent: state.schedulingAssistant.timeSpent,
    canEditCaseForms: userHasRight(CASE_FORMS_MAINTENANCE)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  setCallCenterReady(callCenterReady) {
    dispatch(setCallCenterReady(callCenterReady));
  },
  setCallCenterControlNumber(controlNumber) {
    dispatch(setCallCenterControlNumber(controlNumber));
  },
  beginManageForms(payload) {
    dispatch(manageFormsRedux(payload));
  },
  getCaseForms(id) {
    dispatch(getCaseForms(id));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SchedulingAssistantHeader);
